import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps } from 'react';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Button } from 'Components/frameworks/button/Button';
import { Countdown } from 'Components/frameworks/countdown/Countdown';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Image } from 'Components/frameworks/image/Image';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
export type AnimationEvent = {
  content: {
    button: {
      backgroundColor: Colors;
      label: string;
      textColor: Colors;
      url: string;
    };
    description: string;
    icon: IconName;
    subtitle: string;
    theming: {
      backgroundColor: Colors;
      textColor: Colors;
    };
    title: string;
  };
  visual: {
    countdown?: CMSCountDown;
    imageUrl: string;
    tags?: ReadonlyArray<{
      text: string;
      theming: CMSOffersPushTagTheming;
    }>;
  };
};
type Props = {
  className?: string;
  event: AnimationEvent;
  onClick?: (a: AnimationEvent) => void;
};

const getTags = (countdown, tags = []) => {
  const maxNumberOfTags = countdown?.dateTimeEnd ? 1 : 2;
  const renderedTags = tags.slice(0, maxNumberOfTags).map(({ text, theming }) => (
    <span
      className={classnames(
        'flex items-center rounded-pill px-10 py-5 text-12 font-bold leading-1',
        `bg-${theming.backgroundColor} text-${theming.textColor}`,
      )}
      key={text}
    >
      {text}
    </span>
  ));
  const renderedCountdown = countdown && (
    <Countdown
      {...asWidgetProps<ComponentProps<typeof Countdown>>({
        bgColor: countdown.backgroundColor,
        className: 'text-12 leading-1',
        color: countdown.textColor,
        dataTestId: 'eventAnimation-countdown',
        dateTimeEnd: countdown.dateTimeEnd,
        days: countdown.days,
        hours: countdown.hours,
        minutes: countdown.minutes,
        seconds: countdown.seconds,
        theme: 'offerPush',
      })}
    />
  );

  return [...renderedTags, renderedCountdown];
};

export const EventAnimationSlide = ({ className, event, onClick }: Props) => {
  const { content, visual } = event;
  const hasTags = Boolean(visual.tags?.length || visual.countdown?.dateTimeEnd);
  return (
    <article
      className={classnames(
        className,
        'relative isolate flex flex-col overflow-hidden md:flex-row md:justify-between',
      )}
    >
      <div
        className={classnames(
          'relative isolate min-h-400 space-y-10 rounded-12 px-15 pb-120 pt-60',
          `bg-${content.theming.backgroundColor} text-${content.theming.textColor}`,
          'flex-col md:flex md:w-1/2 md:min-w-1/2 md:justify-center md:rounded-0 md:p-60 md:pe-95 md:ps-80',
        )}
      >
        <Icon
          {...asWidgetProps<ComponentProps<typeof Icon>>({
            className:
              'pointer-events-none absolute left-1/2 top-0 -z-1 mt-40 -translate-x-1/2 text-10 text-white opacity-10 md:left-0 md:text-12',
            name: content.icon,
            width: '25em',
          })}
        />

        <Title
          {...asWidgetProps<ComponentProps<typeof Title>>({
            className: 'text-inherit',
            hLevel: 3,
            theme: 'title-brand-n2',
          })}
        >
          {content.title}
        </Title>
        <Text
          {...asWidgetProps<ComponentProps<typeof Text>>({
            className: 'text-14 text-inherit sm:text-16',
            text: content.description,
            theme: 'none',
          })}
        />
        <div className="flex flex-none items-center gap-20">
          <Text
            {...asWidgetProps<ComponentProps<typeof Text>>({
              className: 'text-14 text-inherit',
              text: content.subtitle,
              theme: 'none',
            })}
          />
          <Button
            {...asWidgetProps<ComponentProps<typeof Button>>({
              className: 'shrink-0',
              color: content.button.textColor,
              colorBg: content.button.backgroundColor,
              onClick: () => onClick?.(event),
              url: content.button.url,
            })}
          >
            {content.button.label}
          </Button>
        </div>
      </div>
      <div className="-mt-100 w-full items-center px-15 md:-ms-70 md:mt-0 md:flex md:min-w-[calc(50%+70px)] md:py-50 md:pe-50 md:ps-0">
        <div className="relative isolate md:w-[calc(100%+20px)]">
          <Image
            {...asWidgetProps<ComponentProps<typeof Image>>({
              className: 'overflow-hidden rounded-12',
              imageUrl: visual.imageUrl,
              ratioKey: ratioUtils.M,
            })}
          />

          {hasTags && (
            <div className="absolute top-0 m-10 flex flex-wrap items-center justify-center gap-5">
              {getTags(visual.countdown, [...(visual.tags || [])] as Parameters<typeof getTags>[1])}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};
